import type { ReactNode } from 'react';

import type { AuditFilterChange, AuditLogEntry, AuditOutcome } from '@/domain/auditLog';

import ActionCell from '../cells/ActionCell';
import ActorCell from '../cells/ActorCell';
import { EntryIdCell, RequestCell, RequestIdCell, UserAgentCell } from '../cells/OptionalCells';
import OutcomeCell from '../cells/OutcomeCell';
import SourceCell from '../cells/SourceCell';
import TargetCell from '../cells/TargetCell';
import TimeCell from '../cells/TimeCell';

import type { AuditColumnId } from './auditLogColumns';

import styles from './AuditLogTable.module.scss';

/** One loaded entry and how many loaded entries share its request (null when alone). */
export interface AuditLogRowData {
    entry: AuditLogEntry;
    requestCount: number | null;
}

interface AuditCellHandlers {
    onSelect: (entry: AuditLogEntry) => void;
    onFilter: (change: AuditFilterChange) => void;
}

// On the first cell, which is always Time: the row itself is only a grid of cells.
export const ACCENT_CLASS: Record<AuditOutcome, string | undefined> = {
    success: undefined,
    denied: styles.accentDenied,
    failed: styles.accentFailed,
};

/** The content of one column's cell for one entry. */
export function renderAuditCell(
    id: AuditColumnId,
    row: AuditLogRowData,
    handlers: AuditCellHandlers
): ReactNode {
    const { entry, requestCount } = row;
    const { onSelect, onFilter } = handlers;
    switch (id) {
        case 'time':
            return <TimeCell entry={entry} onSelect={onSelect} />;
        case 'actor':
            return <ActorCell entry={entry} onFilter={onFilter} />;
        case 'action':
            return <ActionCell entry={entry} requestCount={requestCount} onFilter={onFilter} />;
        case 'target':
            return <TargetCell entry={entry} onFilter={onFilter} />;
        case 'outcome':
            return <OutcomeCell entry={entry} onFilter={onFilter} />;
        case 'source':
            return <SourceCell entry={entry} onFilter={onFilter} />;
        case 'request':
            return <RequestCell entry={entry} />;
        case 'requestId':
            return <RequestIdCell entry={entry} onFilter={onFilter} />;
        case 'userAgent':
            return <UserAgentCell entry={entry} />;
        case 'entryId':
            return <EntryIdCell entry={entry} />;
    }
}

/** Opens the entry, unless the click ended a text selection: copying an IP ends in a click. */
export function selectUnlessCopying(
    entry: AuditLogEntry,
    onSelect: (entry: AuditLogEntry) => void
) {
    if ((window.getSelection()?.toString() ?? '') !== '') return;
    onSelect(entry);
}
